import React from 'react'
import CometChatButton from 'components/base/Button/CometChatButton'
import alertCircleIcon from '@assets/images/product/alert-circle.svg'
import styles from './style.module.scss'

interface ErrorFallbackProps {
  variant: 'app' | 'route'
  error: Error | null
  retryCount: number
  maxRetries: number
  onRetry: () => void
  onGoHome: () => void
  onReload: () => void
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false
  return (
    error.name === 'ChunkLoadError' ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Loading CSS chunk')
  )
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  variant,
  error,
  retryCount,
  maxRetries,
  onRetry,
  onGoHome,
  onReload,
}) => {
  const isChunkError = isChunkLoadError(error)
  const isRetryExhausted = retryCount >= maxRetries

  const containerClassName = [
    styles['cc-error-boundary'],
    variant === 'app' ? styles['cc-error-boundary-app'] : styles['cc-error-boundary-route'],
  ].join(' ')

  const heading = 'Something went wrong'

  const description =
    'An unexpected error occurred. You can try again or go back to the home page.'

  return (
    <div className={containerClassName} role="alert" aria-live="assertive">
      <div className={styles['cc-error-boundary__icon-circle']}>
        <img
          className={styles['cc-error-boundary__icon']}
          src={alertCircleIcon}
          alt="Error"
        />
      </div>

      <h1 className={styles['cc-error-boundary__title']}>{heading}</h1>
      <p className={styles['cc-error-boundary__description']}>{description}</p>

      <div className={styles['cc-error-boundary__actions']}>
        {variant === 'app' || isChunkError ? (
          <>
            <CometChatButton
              hierarchy="primary"
              size="md"
              onClick={onReload}
              htmlType="button"
            >
              Reload Application
            </CometChatButton>
            <CometChatButton
              hierarchy="secondary"
              size="md"
              onClick={onGoHome}
              htmlType="button"
            >
              Go to Home
            </CometChatButton>
          </>
        ) : (
          <>
            <CometChatButton
              hierarchy="primary"
              size="md"
              onClick={onRetry}
              htmlType="button"
              disabled={isRetryExhausted}
            >
              Try Again
            </CometChatButton>
            <CometChatButton
              hierarchy="secondary"
              size="md"
              onClick={onGoHome}
              htmlType="button"
            >
              Go to Home
            </CometChatButton>
          </>
        )}
      </div>

      {isRetryExhausted && variant === 'route' && !isChunkError && (
        <p className={styles['cc-error-boundary__retry-exhausted']}>
          Multiple attempts failed. Please reload the page or contact support.
        </p>
      )}

      {process.env.NODE_ENV === 'development' && error && (
        <div className={styles['cc-error-boundary__stack-trace']}>
          <pre>{error.message}{'\n'}{error.stack}</pre>
        </div>
      )}
    </div>
  )
}

export default ErrorFallback
